import React, { useEffect, useRef, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Copy, LogOut, Wallet, Receipt, TrendingUp, BadgePercent,
  Link2, Download, BarChart2, Calendar, Check, ExternalLink,
  ChevronDown, ChevronUp, QrCode,
} from "lucide-react";
import QRCode from "qrcode";
import { API } from "@/config";
import { useAuth } from "@/context/AuthProvider";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

// ── Static stat card ──────────────────────────────────────────────────────────
const Stat = ({ label, value, testid, accent, sub }) => (
  <div className="glass p-4" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#B9C8D5]">{label}</p>
    <p className={`font-serif text-2xl mt-1 ${accent ? "text-[#FFD166]" : ""}`}>{value}</p>
    {sub && <p className="text-[11px] text-[#B9C8D5] mt-1">{sub}</p>}
  </div>
);

// ── Copy button ───────────────────────────────────────────────────────────────
const CopyBtn = ({ text, label = "Copy", className = "" }) => {
  const [copied, setCopied] = useState(false);
  const click = () => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    toast.success("Copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" onClick={click}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${copied ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-[#FFD166]/40 hover:border-[#FFD166] text-[#FFD166]"} ${className}`}
      data-testid="copy-btn">
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
};

// ── Date filter ───────────────────────────────────────────────────────────────
const PRESETS = [
  { label: "All time", from: null, to: null },
  { label: "This month", from: () => new Date().toISOString().slice(0, 7) + "-01", to: null },
  { label: "Last month", from: () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7) + "-01"; }, to: () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 10); } },
  { label: "Last 30 days", from: () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); }, to: null },
  { label: "Last 90 days", from: () => { const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10); }, to: null },
  { label: "This year", from: () => new Date().getFullYear() + "-01-01", to: null },
];

const DateFilter = ({ dateFrom, dateTo, onDateFrom, onDateTo, onRefresh }) => {
  const [active, setActive] = useState(0);
  const apply = (idx) => {
    const p = PRESETS[idx];
    setActive(idx);
    onDateFrom(typeof p.from === "function" ? p.from() : p.from);
    onDateTo(typeof p.to === "function" ? p.to() : p.to);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="pd-date-filter">
      {PRESETS.map((p, i) => (
        <button key={p.label} type="button" onClick={() => apply(i)}
          className={`px-3 py-1 rounded-full border text-xs ${active === i ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5]"}`}>
          {p.label}
        </button>
      ))}
      <div className="flex items-center gap-1">
        <input type="date" value={dateFrom || ""} onChange={e => { setActive(-1); onDateFrom(e.target.value || null); }}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] text-xs px-2 py-1 rounded" data-testid="pd-filter-from" />
        <span className="text-[#B9C8D5] text-xs">–</span>
        <input type="date" value={dateTo || ""} onChange={e => { setActive(-1); onDateTo(e.target.value || null); }}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] text-xs px-2 py-1 rounded" data-testid="pd-filter-to" />
      </div>
      <button type="button" onClick={onRefresh}
        className="px-2 py-1 rounded border border-[#B9C8D5]/20 text-[#B9C8D5] text-xs hover:border-[#FFD166]/40">
        Refresh
      </button>
    </div>
  );
};

// ── QR Code generator ─────────────────────────────────────────────────────────
const QrDisplay = ({ url }) => {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => {
    if (!url) return;
    QRCode.toDataURL(url, {
      width: 200, margin: 2,
      color: { dark: "#F6F1E4", light: "#0B1B2B" },
    }).then(setDataUrl).catch(() => {});
  }, [url]);

  if (!dataUrl) return null;

  const download = () => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "affiliate-qr.png";
    a.click();
  };

  return (
    <div className="flex flex-col items-center gap-2" data-testid="affiliate-qr">
      <img src={dataUrl} alt="Referral QR code" className="w-32 h-32 rounded-lg" />
      <button type="button" onClick={download}
        className="text-xs text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1">
        <Download className="w-3 h-3" /> Download QR
      </button>
    </div>
  );
};

// ── Commission status pill ────────────────────────────────────────────────────
const CommStatus = ({ status }) => {
  const cls = {
    earned: "bg-emerald-500/15 text-emerald-300",
    paid: "bg-[#FFD166]/15 text-[#FFD166]",
  }[status] || "bg-white/10 text-[#B9C8D5]";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${cls}`}>{status || "—"}</span>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function PartnerDashboard() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showQr, setShowQr] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const qs = params.toString() ? `?${params}` : "";

      const [meRes, bookRes, payRes] = await Promise.all([
        axios.get(`${API}/partners/me${qs}`),
        axios.get(`${API}/partners/me/bookings${qs}`),
        axios.get(`${API}/partners/me/payouts${qs}`),
      ]);
      setData(meRes.data);
      setAnalytics(meRes.data.analytics);
      setBookings(bookRes.data.bookings || []);
      setPayouts(payRes.data.payouts || []);
    } catch (e) {
      if ([401, 403, 404].includes(e.response?.status)) nav("/partners/login", { replace: true });
      else toast.error("Couldn't load your dashboard.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div className="min-h-screen bg-[#0F1E2C] text-[#B9C8D5] flex items-center justify-center">
      Loading…
    </div>
  );

  const { partner, totals } = data;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const referralUrl = partner.referral_token ? `${origin}/?ref=${partner.referral_token}` : null;

  const isFlat = (partner.customer_discount_type || "percent") === "flat";
  const discDesc = isFlat
    ? `$${Number(partner.customer_discount_flat || 0).toFixed(0)} off eligible rental items`
    : (() => {
        const pctVal = Number(partner.customer_discount_percent || 0);
        const maxD = partner.max_customer_discount;
        if (maxD) return `${pctVal.toFixed(0)}% off eligible rental items (max $${Number(maxD).toFixed(0)})`;
        return `${pctVal.toFixed(0)}% off eligible rental items`;
      })();

  const downloadReport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const qs = params.toString() ? `?${params}` : "";
      const r = await axios.get(`${API}/partners/me/report${qs}`);
      const report = r.data;

      const lines = [
        `AFFILIATE PERFORMANCE REPORT`,
        `${report.partner.business_name}`,
        `Generated: ${new Date().toLocaleDateString()}`,
        `Period: ${dateFrom || "All time"} – ${dateTo || "Present"}`,
        ``,
        `PROMO CODE: ${report.partner.code}`,
        `CUSTOMER OFFER: ${report.partner.customer_offer}`,
        `COMMISSION RATE: ${report.partner.commission_percent}%`,
        ``,
        `ANALYTICS`,
        `Total link clicks: ${report.analytics.total_clicks}`,
        `Unique sessions: ${report.analytics.unique_sessions}`,
        `Qualifying rentals: ${report.analytics.qualifying_bookings}`,
        `Conversion rate: ${report.analytics.conversion_rate}%`,
        ``,
        `FINANCIALS`,
        `Eligible rental volume: $${(report.totals.eligible_sales || 0).toFixed(2)}`,
        `Customer discounts given: $${(report.totals.customer_discounts || 0).toFixed(2)}`,
        `Commission earned: $${(report.totals.commission_earned || 0).toFixed(2)}`,
        `Commission paid: $${(report.totals.commission_paid || 0).toFixed(2)}`,
        `Balance owed: $${(report.totals.balance_owed || 0).toFixed(2)}`,
        ``,
        `ORDER DETAIL`,
        `Order,Delivery Date,Pickup Date,Status,Eligible Subtotal,Customer Discount,Commissionable,Commission,Commission Status`,
        ...(report.bookings || []).map(b =>
          `${b.order_number},${b.delivery_date},${b.pickup_date},${b.status},$${b.eligible_subtotal?.toFixed(2)},$${b.customer_discount?.toFixed(2)},$${b.commissionable_subtotal?.toFixed(2)},$${b.commission?.toFixed(2)},${b.commission_status}`
        ),
      ];

      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate-report-${dateFrom || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't generate report.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1E2C] text-[#F6F1E4] py-12 px-4">
      <Helmet><title>Affiliate dashboard — Enchanted Gear Rentals</title></Helmet>

      <div className="max-w-5xl mx-auto" data-testid="partner-dashboard">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#B9C8D5] mb-2">Affiliate dashboard</div>
            <h1 className="font-serif text-4xl">{partner.business_name}</h1>
            <p className="text-sm text-[#B9C8D5] mt-1">{partner.contact_name} · {partner.email}</p>
          </div>
          <button type="button" onClick={async () => { await logout(); nav("/partners/login"); }}
            className="btn-ghost inline-flex items-center gap-2" data-testid="partner-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {/* YOUR MEMBER OFFER */}
        <div className="glass p-5 mb-6" data-testid="partner-offer-card">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#B9C8D5] mb-3">Your member offer</p>
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="font-serif text-3xl text-[#FFD166]">{discDesc.split(" ")[0]}</p>
              <p className="text-sm text-[#B9C8D5]">{discDesc.replace(discDesc.split(" ")[0] + " ", "")}</p>
              {partner.code && (
                <div className="flex items-center gap-3 mt-3">
                  <div>
                    <p className="text-[10px] text-[#B9C8D5] uppercase tracking-widest">Promo Code</p>
                    <p className="font-mono text-2xl text-[#FFD166]" data-testid="partner-code">{partner.code}</p>
                  </div>
                  <CopyBtn text={partner.code} label="Copy code" />
                </div>
              )}
            </div>

            {/* Referral link */}
            {referralUrl && (
              <div className="flex-1 min-w-64">
                <p className="text-[10px] text-[#B9C8D5] uppercase tracking-widest mb-2">Referral link</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-mono text-xs text-[#FFD166] break-all" data-testid="partner-referral-url">{referralUrl}</p>
                  <CopyBtn text={referralUrl} label="Copy link" />
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <button type="button" onClick={() => setShowQr(!showQr)}
                    className="inline-flex items-center gap-1 text-xs text-[#B9C8D5] hover:text-[#FFD166]">
                    <QrCode className="w-3.5 h-3.5" /> {showQr ? "Hide QR" : "Show QR code"}
                  </button>
                  {analytics && (
                    <span className="text-xs text-[#B9C8D5]">
                      {analytics.total_clicks} clicks · {analytics.unique_sessions} unique
                    </span>
                  )}
                </div>
                {showQr && <div className="mt-3"><QrDisplay url={referralUrl} /></div>}
              </div>
            )}
          </div>
          <p className="text-xs text-[#B9C8D5] mt-4">
            Commission rate: <span className="text-[#FFD166]">{Number(partner.commission_percent || 0).toFixed(1)}%</span> of commissionable rental revenue
            (eligible subtotal after customer discount, before tax).
            Rates are set by Enchanted Gear Rentals and may not be modified.
          </p>
          {partner.start_date && (
            <p className="text-xs text-[#B9C8D5] mt-1">
              Partnership since: {partner.start_date}
              {partner.review_date && ` · Review: ${partner.review_date}`}
              {partner.end_date && ` · Ends: ${partner.end_date}`}
            </p>
          )}
        </div>

        {/* Date filter */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <DateFilter
            dateFrom={dateFrom} dateTo={dateTo}
            onDateFrom={setDateFrom} onDateTo={setDateTo}
            onRefresh={load}
          />
          <button type="button" onClick={downloadReport}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40 hover:text-[#FFD166]"
            data-testid="pd-download-report">
            <Download className="w-3.5 h-3.5" /> Download report
          </button>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <Stat label="Qualifying rentals" value={totals.bookings} testid="pd-bookings" />
          <Stat label="Eligible volume" value={money(totals.eligible_sales)} testid="pd-sales" />
          <Stat label="Customer savings" value={money(totals.customer_discounts)} testid="pd-customer-savings" />
          <Stat label="Commission earned" value={money(totals.commission_earned)} testid="pd-earned" accent />
          <Stat label="Commission paid" value={money(totals.commission_paid)} testid="pd-paid" />
          <Stat label="Current balance" value={money(Math.max(0, totals.balance_owed))} testid="pd-balance" accent />
        </div>

        {/* Analytics */}
        {analytics && (
          <div className="glass p-5 mb-6" data-testid="pd-analytics">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 className="w-4 h-4 text-[#FFD166]" />
              <h2 className="font-serif text-xl">Referral analytics</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                ["Link clicks", analytics.total_clicks, "pd-clicks"],
                ["Unique visitors", analytics.unique_sessions, "pd-sessions"],
                ["Qualifying rentals", analytics.qualifying_bookings, "pd-conv-rentals"],
                ["Conversion rate", pct(analytics.conversion_rate), "pd-conversion"],
              ].map(([l, v, t]) => (
                <div key={l} data-testid={t}>
                  <p className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">{l}</p>
                  <p className="font-semibold text-xl mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#B9C8D5] mt-3">
              Conversion rate = qualifying rentals ÷ unique referral sessions.
              Counts only rentals that have actually started — future or pre-cancelled bookings are not included.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            ["overview", Receipt, "Rentals"],
            ["payouts", Wallet, "Payouts"],
          ].map(([k, Icon, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-full border text-sm inline-flex items-center gap-2 ${tab === k ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5]"}`}
              data-testid={`pd-tab-${k}`}>
              <Icon className="w-4 h-4" /> {l}
              {k === "overview" && bookings.length > 0 && (
                <span className="px-1.5 py-0.5 bg-[#FFD166]/20 rounded-full text-[10px]">{bookings.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Rentals tab */}
        {tab === "overview" && (
          <div className="glass p-4 overflow-x-auto" data-testid="pd-bookings-table">
            {bookings.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-[#B9C8D5]">No qualifying rentals yet.</p>
                <p className="text-xs text-[#B9C8D5] mt-2">Rentals appear here once they have started.</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#B9C8D5] mb-3">
                  Showing {bookings.length} qualifying rental{bookings.length !== 1 ? "s" : ""}.
                  Rentals appear after their start date and exclude pre-start cancellations.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#B9C8D5] border-b border-[#FFD166]/15">
                      <th className="py-2 pr-3">Order</th>
                      <th className="py-2 pr-3">Rental dates</th>
                      <th className="py-2 pr-3">Eligible subtotal</th>
                      <th className="py-2 pr-3">Customer discount</th>
                      <th className="py-2 pr-3">Commissionable</th>
                      <th className="py-2 pr-3">Commission %</th>
                      <th className="py-2 pr-3">Commission earned</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2">Booking status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map(b => (
                      <tr key={b.reservation_id} className="border-b border-[#FFD166]/5"
                        data-testid={`pd-booking-${b.reservation_id}`}>
                        <td className="py-2 pr-3 font-mono text-[#FFD166]">{b.order_number}</td>
                        <td className="py-2 pr-3 text-xs text-[#B9C8D5]">{b.delivery_date} → {b.pickup_date}</td>
                        <td className="py-2 pr-3">{money(b.eligible_subtotal)}</td>
                        <td className="py-2 pr-3 text-amber-300">-{money(b.customer_discount)}</td>
                        <td className="py-2 pr-3">{money(b.commissionable_subtotal)}</td>
                        <td className="py-2 pr-3 text-[#B9C8D5]">{pct(b.commission_percent)}</td>
                        <td className="py-2 pr-3 text-[#FFD166]">
                          {money(b.commission)}
                          {b.adjustment !== 0 && (
                            <span className="text-xs text-red-300 ml-1">({money(b.adjustment)} adj.)</span>
                          )}
                        </td>
                        <td className="py-2 pr-3"><CommStatus status={b.commission_status} /></td>
                        <td className="py-2 capitalize text-xs">{b.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Totals row */}
                <div className="border-t border-[#FFD166]/20 mt-3 pt-3 flex flex-wrap gap-6 text-sm">
                  <div><span className="text-[#B9C8D5]">Eligible: </span><span>{money(totals.eligible_sales)}</span></div>
                  <div><span className="text-[#B9C8D5]">Discounts: </span><span className="text-amber-300">-{money(totals.customer_discounts)}</span></div>
                  <div><span className="text-[#B9C8D5]">Earned: </span><span className="text-[#FFD166] font-semibold">{money(totals.commission_earned)}</span></div>
                  <div><span className="text-[#B9C8D5]">Paid: </span><span>{money(totals.commission_paid)}</span></div>
                  <div><span className="text-[#B9C8D5]">Balance: </span><span className="text-amber-300 font-semibold">{money(Math.max(0, totals.balance_owed))}</span></div>
                </div>
                <p className="text-[11px] text-[#B9C8D5] mt-2">
                  Commission is calculated on the eligible rental/merchandise subtotal AFTER the customer discount and BEFORE tax.
                  Commission adjusts automatically for refunds and cancellations.
                </p>
              </>
            )}
          </div>
        )}

        {/* Payouts tab */}
        {tab === "payouts" && (
          <div className="glass p-4 overflow-x-auto" data-testid="pd-payouts-table">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm">Current balance: <span className="text-[#FFD166] font-semibold" data-testid="pd-payout-balance">{money(Math.max(0, totals.balance_owed))}</span></p>
                <p className="text-xs text-[#B9C8D5]">Total paid: {money(totals.commission_paid)}</p>
              </div>
            </div>
            {payouts.length === 0 ? (
              <p className="text-sm text-[#B9C8D5] py-6 text-center">No payouts yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#B9C8D5] border-b border-[#FFD166]/15">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Amount</th>
                    <th className="py-2 pr-3">Method</th>
                    <th className="py-2 pr-3">Orders</th>
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map(p => (
                    <tr key={p.id} className="border-b border-[#FFD166]/5">
                      <td className="py-2 pr-3">{p.paid_on}</td>
                      <td className="py-2 pr-3 text-[#FFD166] font-semibold">{money(p.amount)}</td>
                      <td className="py-2 pr-3 text-[#B9C8D5] capitalize">{p.payment_method || "paypal"}</td>
                      <td className="py-2 pr-3 text-xs text-[#B9C8D5]">{p.order_ids?.length || "—"} orders</td>
                      <td className="py-2 pr-3 font-mono text-xs">{p.reference || "—"}</td>
                      <td className="py-2">
                        <span className="px-1.5 py-0.5 bg-[#FFD166]/15 text-[#FFD166] rounded text-[10px] uppercase">{p.status || "paid"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <p className="text-xs text-[#B9C8D5] mt-6 inline-flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" />
          Commission adjusts automatically on refunds.
          <BadgePercent className="w-3.5 h-3.5 ml-2" />
          All discount and commission rates are managed by Enchanted Gear Rentals.
        </p>
      </div>
    </div>
  );
}
